import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Unlock, Calendar, Image, Eye } from 'lucide-react';

const FolderGrid = ({ folders, viewMode }) => {
  const navigate = useNavigate();

  const handleFolderClick = (folder) => {
    if (folder.isSecure) {
      navigate(`/verify/${folder.id}`);
    } else {
      navigate(`/events/${folder.id}/photos`);
    }
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Folder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Photos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Security
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modified
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {folders.map(folder => (
                <tr key={folder.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleFolderClick(folder)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={folder.thumbnail}
                        alt={folder.name}
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{folder.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {folder.photoCount} photos
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      folder.isSecure
                        ? 'bg-red-100 text-red-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {folder.isSecure ? (
                        <>
                          <Lock className="h-3 w-3 mr-1" />
                          Secure
                        </>
                      ) : (
                        <>
                          <Unlock className="h-3 w-3 mr-1" />
                          Public
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {folder.lastModified}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFolderClick(folder);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {folders.map(folder => (
        <div
          key={folder.id}
          onClick={() => handleFolderClick(folder)}
          className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
        >
          {/* Thumbnail */}
          <div className="aspect-video relative overflow-hidden">
            <img
              src={folder.thumbnail}
              alt={folder.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            
            {/* Security Badge */}
            <div className="absolute top-3 right-3">
              {folder.isSecure ? (
                <div className="bg-red-500 text-white p-2 rounded-full shadow-lg">
                  <Lock className="h-4 w-4" />
                </div>
              ) : (
                <div className="bg-emerald-500 text-white p-2 rounded-full shadow-lg">
                  <Unlock className="h-4 w-4" />
                </div>
              )}
            </div>

            {/* Photo Count */}
            <div className="absolute bottom-3 left-3">
              <div className="bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center">
                <Image className="h-4 w-4 mr-1" />
                {folder.photoCount}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
              {folder.name}
            </h3>
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="h-4 w-4 mr-1" />
              Modified {folder.lastModified}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FolderGrid;